import type { ChartOfAccount, DefaultAccountSlot } from '@clockwise/clients/chart-of-accounts';
import { ApiError } from '@clockwise/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { NoAccess } from '@/components/custom/no-access';
import { SearchInput } from '@/components/custom/search-input';
import { StickyTableScroll } from '@/components/custom/sticky-table-scroll';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useChartOfAccounts } from '@/lib/chart-of-accounts';
import { useMe } from '@/lib/me';
import { useHasPermission } from '@/lib/permissions';

const listSearch = z.object({
  q: z.string().optional(),
  status: z.enum(['active', 'inactive', 'all']).optional(),
});
type ListSearch = z.infer<typeof listSearch>;

export const Route = createFileRoute('/_authenticated/setup/chart-of-accounts')({
  validateSearch: listSearch,
  component: ChartOfAccountsPage,
});

const DEFAULT_LABEL: Record<DefaultAccountSlot, string> = {
  retainer: 'retainer fees',
  service: 'service lines',
  expense: 'expenses',
  discount: 'discounts',
};

const TYPE_LABEL: Record<ChartOfAccount['accountType'], string> = {
  income: 'Income',
  contra_income: 'Contra-income',
};

/**
 * Setup → Chart of accounts. Read-only: the chart is maintained in the books,
 * not in Clockwise. The whole chart is a few dozen rows, so it is fetched once
 * and filtered here; search and status live in the URL so a filtered view can
 * be bookmarked.
 */
function ChartOfAccountsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const q = search.q ?? '';
  const status = search.status ?? 'active';
  const updateSearch = (next: Partial<ListSearch>) =>
    navigate({ search: (prev: ListSearch) => stripDefaults({ ...prev, ...next }), replace: true });

  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => setSearchInput(q), [q]);
  useEffect(() => {
    if (searchInput === q) return;
    const handle = setTimeout(() => updateSearch({ q: searchInput }), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, q]);

  const query = useChartOfAccounts();
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return query.accounts.filter(
      (a) =>
        (status === 'all' || a.active === (status === 'active')) &&
        (!needle || `${a.code} ${a.name}`.toLowerCase().includes(needle)),
    );
  }, [query.accounts, q, status]);

  const filtered = q.trim() !== '' || status !== 'active';

  const canView = useHasPermission('chart_of_accounts.view');
  const { loading: meLoading } = useMe();
  if (!meLoading && !canView) {
    return <NoAccess permission="chart_of_accounts.view" resource="Chart of accounts" />;
  }

  return (
    <PageShell>
      <PageHeader
        title="Chart of accounts"
        subtitle="The income accounts invoice lines are booked to."
        count={rows.length}
      >
        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
          <Lock className="size-3.5" aria-hidden />
          Read-only. Maintained outside Clockwise.
        </span>
      </PageHeader>

      <div className="flex items-center gap-3">
        <SearchInput
          placeholder="Search code or name…"
          value={searchInput}
          onValueChange={setSearchInput}
        />
        <Select
          value={status}
          onValueChange={(v) => updateSearch({ status: v as ListSearch['status'] })}
        >
          <SelectTrigger className="w-36" aria-label="Status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <StickyTableScroll>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-28">Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-36">Type</TableHead>
              <TableHead>Used by</TableHead>
              <TableHead className="w-28">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody aria-busy={query.isLoading}>
            {query.isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {Array.from({ length: 5 }).map((__, j) => (
                    <TableCell key={`skeleton-cell-${i}-${j}`}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : query.isError ? (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center">
                  <p className="text-destructive">
                    {query.error instanceof ApiError
                      ? query.error.message
                      : 'Could not load the chart of accounts.'}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => query.refetch()}
                  >
                    Retry
                  </Button>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                  {filtered ? (
                    <>
                      No accounts match these filters.{' '}
                      <Button
                        variant="link"
                        className="h-auto p-0"
                        onClick={() => updateSearch({ q: undefined, status: undefined })}
                      >
                        Clear filters
                      </Button>
                    </>
                  ) : (
                    'No accounts yet. The chart of accounts is loaded from the books.'
                  )}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-mono tabular-nums">{a.code}</TableCell>
                  <TableCell>{a.name}</TableCell>
                  <TableCell>{TYPE_LABEL[a.accountType]}</TableCell>
                  <TableCell>
                    <UsedBy account={a} />
                  </TableCell>
                  <TableCell>
                    {a.active ? (
                      <Badge variant="secondary">Active</Badge>
                    ) : (
                      <Badge variant="outline">Inactive</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StickyTableScroll>
    </PageShell>
  );
}

function UsedBy({ account }: { account: ChartOfAccount }) {
  const parts: string[] = [];
  if (account.activityCount > 0) {
    parts.push(
      `${account.activityCount} ${account.activityCount === 1 ? 'activity' : 'activities'}`,
    );
  }
  if (account.defaultFor.length > 0) {
    parts.push(`default for ${account.defaultFor.map((d) => DEFAULT_LABEL[d]).join(', ')}`);
  }
  if (parts.length === 0) return <span className="text-muted-foreground">Not used</span>;
  return <span>{parts.join('; ')}</span>;
}

function stripDefaults(s: ListSearch): ListSearch {
  const out: ListSearch = {};
  if (s.q && s.q.length > 0) out.q = s.q;
  if (s.status && s.status !== 'active') out.status = s.status;
  return out;
}
